
import React from 'react';
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Settings2, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';

interface PointsDeductionDialogProps {
  team: string;
  season: string;
  league: string;  // Add league prop
  deductionId?: number;
  initialPoints?: string;
  initialReason?: string;
  isEdit?: boolean;
  onDelete?: () => Promise<void>;
}

export function PointsDeductionDialog({
  team,
  season,
  league,  // Add league to props
  deductionId,
  initialPoints = '',
  initialReason = '',
  isEdit = false,
  onDelete
}: PointsDeductionDialogProps) {
  const [open, setOpen] = React.useState(false);
  const [points, setPoints] = React.useState(initialPoints);
  const [reason, setReason] = React.useState(initialReason);
  const [isLoading, setIsLoading] = React.useState(false);

  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isEdit && deductionId) {
        const { error } = await supabase
          .from('points_deductions')
          .update({
            points: parseInt(points),
            reason,
            league  // Include league in update
          })
          .eq('id', deductionId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('points_deductions')
          .insert([
            {
              team,
              season,
              league,  // Include league in insert
              points: parseInt(points),
              reason,
              created_by: user?.id
            }
          ]);

        if (error) throw error;
      }

      toast({
        title: "Success",
        description: `Points deduction ${isEdit ? 'updated' : 'added'} successfully`,
      });

      queryClient.invalidateQueries({ queryKey: ['points-deductions'] });
      setOpen(false);
    } catch (error) {
      console.error('Error saving points deduction:', error);
      toast({
        title: "Error",
        description: "Failed to save points deduction",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
        >
          {isEdit ? <Settings2 className="h-4 w-4" /> : <span>+</span>}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit' : 'Add'} Points Deduction</DialogTitle>
          <DialogDescription>
            {isEdit 
              ? `Update points deduction for ${team}`
              : `Add a points deduction for ${team}`
            }
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="points">Points to Deduct</Label>
              <Input
                id="points"
                type="number"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="reason">Reason</Label>
              <Input
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason for points deduction"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            {isEdit && onDelete && (
              <Button
                type="button"
                variant="destructive"
                onClick={onDelete}
                disabled={isLoading}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            )}
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
